import { Instagram, Facebook, Heart } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { WhatsAppIcon } from "./WhatsAppIcon";

export const Footer = ({ businessInfo }) => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSectionNav = (hash) => {
    if (location.pathname !== "/") {
      navigate("/" + hash);
    } else {
      document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <footer
      data-testid="footer"
      className="bg-[#1A1A1A] text-white py-16 md:py-24 px-6 md:px-12 lg:px-24"
    >
      <div className="max-w-7xl mx-auto">
        {/* Main Footer Content */}
        <div className="grid md:grid-cols-3 gap-12 md:gap-8 mb-16">
          {/* Brand */}
          <div>
            <h3
              className="font-logo text-4xl md:text-5xl mb-4"
              data-testid="footer-brand"
            >
              Urban Beauty
            </h3>
            <p className="text-white/60 leading-relaxed">
              Premium Beauty Salon in Klosters.
              <br />
              Von Frauen gegründet, für Schönheit gemacht.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm tracking-wider uppercase mb-6 text-[#C5A059]">
              Navigation
            </h4>
            <ul className="space-y-3">
              {[
                { href: "#about", label: "Über uns" },
                { href: "#services", label: "Behandlungen" },
                { href: "#gallery", label: "Galerie" },
                { href: "#reviews", label: "Bewertungen" },
                { href: "#contact", label: "Kontakt" },
              ].map((link) => (
                <li key={link.href}>
                  <button
                    onClick={() => handleSectionNav(link.href)}
                    className="text-white/60 hover:text-white transition-colors duration-300"
                    data-testid={`footer-link-${link.href.slice(1)}`}
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-sm tracking-wider uppercase mb-6 text-[#C5A059]">
              Kontakt
            </h4>
            {businessInfo && (
              <div className="space-y-3 text-white/60">
                <p data-testid="footer-address">{businessInfo.address}</p>
                <p>
                  <a
                    href={`tel:${businessInfo.phone.replace(/\s/g, "")}`}
                    className="hover:text-white transition-colors"
                    data-testid="footer-phone"
                  >
                    {businessInfo.phone}
                  </a>
                </p>
              </div>
            )}

            {/* Social Links */}
            {businessInfo && (
              <div className="flex gap-4 mt-6" data-testid="footer-social">
                <a
                  href={businessInfo.social.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/60 hover:bg-[#E1306C] hover:text-white hover:border-[#E1306C] transition-all duration-300"
                  aria-label="Instagram"
                  data-testid="footer-instagram"
                >
                  <Instagram size={18} />
                </a>
                <a
                  href={businessInfo.social.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/60 hover:bg-[#1877F2] hover:text-white hover:border-[#1877F2] transition-all duration-300"
                  aria-label="Facebook"
                  data-testid="footer-facebook"
                >
                  <Facebook size={18} />
                </a>
                {businessInfo.social.whatsapp && (
                  <a
                    href={businessInfo.social.whatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/60 hover:bg-[#25D366] hover:text-white hover:border-[#25D366] transition-all duration-300"
                    aria-label="WhatsApp"
                    data-testid="footer-whatsapp"
                  >
                    <WhatsAppIcon size={18} />
                  </a>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Divider */}
        <div className="w-full h-[1px] bg-white/10 mb-8" />

        {/* Bottom Footer */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <p
            className="text-white/60 text-sm"
            data-testid="footer-copyright"
          >
            © {currentYear} Urban Beauty Klosters. Alle Rechte vorbehalten.
          </p>

          {/* Legal links grouped in one compact row */}
          <div
            className="flex items-center gap-3 text-white/60 text-sm"
            data-testid="footer-legal-links"
          >
            <Link
              to="/impressum"
              className="hover:text-white transition-colors duration-300"
              data-testid="footer-link-impressum"
            >
              Impressum
            </Link>
            <span className="text-white/20">·</span>
            <Link
              to="/datenschutz"
              className="hover:text-white transition-colors duration-300"
              data-testid="footer-link-datenschutz"
            >
              Datenschutz
            </Link>
            <span className="text-white/20">·</span>
            <Link
              to="/agb"
              className="hover:text-white transition-colors duration-300"
              data-testid="footer-link-agb"
            >
              AGB
            </Link>
          </div>

          <p className="text-white/60 text-sm flex items-center gap-2">
            Mit <Heart size={14} className="text-[#C5A059]" /> in Klosters
          </p>
        </div>

        {/* Large Brand Text */}
        <div className="mt-16 overflow-hidden">
          <p
            className="font-playfair text-6xl md:text-8xl lg:text-9xl text-white/5 text-center whitespace-nowrap select-none"
            aria-hidden="true"
          >
            Urban Beauty
          </p>
        </div>
      </div>
    </footer>
  );
};
